import { useToast } from '~/components/toast-context'
import { entryLogged, type Amount, type EntryLogging, type Food } from '~/data/nutrition'
import { logEntries } from '~/data/nutrition-store'
import { recordDiaryStep } from '~/lib/diary-undo'

/**
 * Logging onto a day, wherever what is logged was found: a food's own page, the header's
 * search on the food log, a quick entry, what the AI made of a photograph. The entries stand on
 * the day in the same press and are sent behind it, so the key that logs never waits on the
 * server — the nutrition spec, Logging an entry.
 *
 * It is one hook rather than one copy per way in, because "logged" means the same thing every
 * time: the entries are kept, the diary can undo them, and it is said.
 */
export function useFoodLogger(): {
  logFood: (food: Food, amount: Amount, when: { day: string; time: string }) => void
  /**
   * Several at once, as one press — what the AI made of one photograph, or a quick entry. The
   * step is said as [said] where there is one entry and as a count where there are more.
   */
  logEntries: (toLog: readonly EntryLogging[]) => void
} {
  const toast = useToast()

  const log = (toLog: readonly EntryLogging[]) => {
    /*
     * Counted up through the press, so the entries it makes stand in the order they were
     * logged: a day orders one minute's entries by when each was written, and a tie would leave
     * them to their random ids.
     */
    const at = Date.now()
    const made = toLog.map((each, index) => entryLogged(each, at + index))
    logEntries(made)
    // One press, one step, however many entries it made.
    const first = made[0]
    const said = made.length === 1 ? (first?.name ?? 'Food') : `${String(made.length)} foods`
    recordDiaryStep({
      said: `${said} logged`,
      changes: made.map((entry) => ({ id: entry.id, before: null, after: entry })),
    })
    toast(`${said} logged.`)
  }

  return {
    logFood: (food, amount, when) => {
      log([{ day: when.day, time: when.time, food, amount }])
    },
    logEntries: log,
  }
}
